import React, {useState, useEffect, Fragment } from "react";
import join from "ramda/src/join";
import compose from "ramda/src/compose";
import styled from "@emotion/styled";

import { getReadbleDate } from "../../helpers/getReadbleDate";
import { useNetwork } from "../../hooks/useNetwork";
import { useGetRoomSyncFloorDetails } from "./useGetRoomSyncFloorPlan";
import { useSelectedNetworkContext } from "../../contexts/NetworkContext/context";
import { EnhancedTableBody } from "../EnhancedTableBody";
import { TableRow } from "../../UI/TableRow";
import { TableCell } from "../../UI/TableCell";
import { Typography } from "../../UI/Typography";
import { COLOR_HAWKES_BLUE, COLOR_ZUMTHOR } from "../../constants/styles";
import { useEntrataIntegrationContext } from "./EntrataIntegrationProvider";
import { usePageTablePaginationContext } from "../PageTablePagination/context";
import { usePaginationHandler } from "../../hooks/usePaginationHandler";
import { IIntegrationsEntrata } from "../../services/IntegrationApi";


interface IProps {
  showArchived?: boolean;
}


const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DATE_TYPE_MAPPING = {
  START_DATE: "Lease Starts",
  END_DATE: "End Date",
};

const getDate = compose(getReadbleDate("YYYY/M/D"), join("/"));

const getRoomsyncTableCellSX = (idx: number) => ({
  bgcolor: idx % 2 ? COLOR_ZUMTHOR : COLOR_HAWKES_BLUE,
});

export const IntegrationTableEntrataBody: React.FC<IProps> = ({showArchived}) => {
  const { getL2 } = useNetwork();
  const { loadIntegrations, integrations } = useEntrataIntegrationContext();
  const { selectedNetworkId } = useSelectedNetworkContext();
  const getRoomSyncFloorDetails = useGetRoomSyncFloorDetails();

  const [isLoading, setIsLoading] = React.useState(false);
  const { page, setPage, perPage, setPages } = usePageTablePaginationContext();
  const [filteredIntegrations, setFilteredIntegrations] = useState(integrations.mappings);
  const { selectedRows, pages } = usePaginationHandler(
    filteredIntegrations,
    page,
    perPage
  );

  const sortedRows = React.useMemo(() => selectedRows.sort((a, b) => {
    // First, compare by l2Name
    const l2NameA = getRoomSyncFloorDetails(a).l2Name;
    const l2NameB = getRoomSyncFloorDetails(b).l2Name;
    const l2Compare = (l2NameA || '').localeCompare(l2NameB || '');
    if (l2Compare !== 0) return l2Compare;

    // If l2Names are equal, compare by entrataFloorPlan
    const floorPlanCompare = (a.entrataFloorPlan || '').localeCompare(b.entrataFloorPlan || '');
    if (floorPlanCompare !== 0) return floorPlanCompare;

    // If entrataFloorPlans are equal, compare by entrataSpaceConfiguration
    return (a.entrataSpaceConfiguration || '').localeCompare(b.entrataSpaceConfiguration || '');
  }), [selectedRows, getRoomSyncFloorDetails]);
  const showRow = (l1Id: number, l2Id: number) => {
    if(showArchived) {
      return true;
    }
    return getL2(l1Id, l2Id)?.matchingRoundStatus !== "ARCHIVED";
  };

  useEffect(() => {
    const filteredRows = integrations.mappings.filter(r => r && showRow(r.l1Id, r.l2Id));
    setFilteredIntegrations(filteredRows);
  }, [showArchived, integrations]);

  React.useEffect(() => {
    const getData = async () => {
      setPage(1);
      setIsLoading(true);
      await loadIntegrations(selectedNetworkId);
      setIsLoading(false);
    };

    getData();
  }, [selectedNetworkId]);

  React.useEffect(() => {
    setPages(pages);
  }, [pages.length]);

  const renderRow = React.useCallback((row: IIntegrationsEntrata, idx) => {
    const floorDetails = getRoomSyncFloorDetails(row);

    return (
      <Fragment key={idx}>
        {sortedRows[idx - 1]?.l2Id !== row.l2Id
          ? (
            <TableRow key={row.l2Id}>
              <TableCell sx={getRoomsyncTableCellSX(idx)}>
                <Typography variant="subtitle2">
                  <b>{floorDetails.l2Name}</b>
                </Typography>
              </TableCell>
              <TableCell sx={getRoomsyncTableCellSX(idx)} />
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>
          )
          : null}
        {row?.l4Id && sortedRows[idx - 1]?.l3Id !== row.l3Id
          ? (
            <TableRow key={row.l3Id}>
              <TableCell sx={getRoomsyncTableCellSX(idx)}>
                <Typography ml={3} variant="subtitle2">
                  <b>{floorDetails.l3Name}</b>
                </Typography>
              </TableCell>
              <TableCell sx={getRoomsyncTableCellSX(idx)}>
                {floorDetails.l3Size? (floorDetails.l3Size+1): '-'}
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>
          )
          : null}
        <TableRow key={row.id}>
          <TableCell sx={getRoomsyncTableCellSX(idx)}>
            <Typography ml={row.l4Id ? 6 : 3} variant="subtitle2">
              <b>
                {
                  row.l4Id
                    ? floorDetails.l4Name
                    : floorDetails.l3Name
                }
              </b>
            </Typography>
          </TableCell>
          <TableCell sx={getRoomsyncTableCellSX(idx)}>
            {floorDetails.l4Size ? floorDetails.l4Size : floorDetails.l3Size? (floorDetails.l3Size+1): '-'}
          </TableCell>
          <TableCell>{row.entrataFloorPlan}</TableCell>
          <TableCell>{row.entrataUnitSpace}</TableCell>
          <TableCell>{row.entrataSpaceConfiguration || '-'}</TableCell>
          <TableCell>
            <Row>
              <Typography variant="body2" align='left'>
                {DATE_TYPE_MAPPING[row.dateType]}
              </Typography>
              <Typography variant="body2" align='right'>
                {`${row.entrataStartDate ? getDate(row.entrataStartDate) : <i>no date</i>} - ${row.entrataEndDate ? getDate(row.entrataEndDate) : <i>no date</i>}`}
              </Typography>
            </Row>
          </TableCell>
        </TableRow>
      </Fragment>
    );
  }, [sortedRows]);

  return (
    <EnhancedTableBody colSpan={8} isLoading={isLoading}>
      {sortedRows.map(renderRow)}
    </EnhancedTableBody>
  );
};
